import React from 'react';
import { Box, Grid, useMediaQuery } from '@mui/material';
import { useTheme } from '@emotion/react';
import Shimmer from 'ui-components/Shimmer';

// Skeleton for the two Settings tabs DealFlow.js renders: thesis / deal-flow.
const CARD_PADDING = '1.25rem';

// Control heights taken from the real components.
const THESIS_INPUT = 48;
const EMAIL_INPUT = 55;
const CRITERIA_ROWS = 100;
const TOOLBAR = 38;

const LABEL_GAP = '8px';

const Label = ({ width }) => (
  <Shimmer width={width} height={12} radius={4} sx={{ mb: LABEL_GAP }} />
);

const Heading = ({ width }) => (
  <Shimmer width={width} height={22} radius={4} sx={{ mb: 1 }} />
);

const SaveButton = () => (
  <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
    <Shimmer width={150} height={50} radius={8} sx={{ mt: '1.5rem' }} />
  </Box>
);

// Same wrapper chain as the real cards, so placement matches.
const Card = ({ theme, children }) => (
  <Box sx={{ maxWidth: '900px', marginInline: 'auto', width: '100%' }}>
    <Box
      sx={{
        border: `1px solid ${theme.palette.customColors.b50}`,
        borderRadius: '8px',
        background: theme.palette.customColors.whiteBG,
        padding: CARD_PADDING,
        width: '-webkit-fill-available',
      }}
    >
      {children}
    </Box>
  </Box>
);

const EditorBlock = ({ theme, bodyHeight }) => (
  <Box
    sx={{
      border: `1px solid ${theme.palette.customColors.g75}`,
      borderRadius: '8px',
      overflow: 'hidden',
    }}
  >
    <Box sx={{ height: bodyHeight, padding: '12px' }}>
      <Shimmer width="60%" height={13} radius={4} />
    </Box>
    <Box
      sx={{
        height: TOOLBAR,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 12px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Shimmer key={i} width={18} height={18} radius={4} />
        ))}
      </Box>
      {/* "-8 characters left" */}
      <Shimmer width={96} height={12} radius={4} />
    </Box>
  </Box>
);

const CHIP_WIDTHS = [104, 118, 120, 62];

const ChipRow = () => (
  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
    <Shimmer width={34} height={13} radius={4} />
    {CHIP_WIDTHS.map((width, i) => (
      <Shimmer key={i} width={width} height={32} radius={16} />
    ))}
  </Box>
);

const THESIS_FIELDS = [130, 115, 52, 76, 56, 118];

const ThesisCard = ({ theme }) => (
  <Card theme={theme}>
    {/* "Investment Thesis" + the Auto-Shortlist label and switch */}
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 1 }}>
      <Shimmer width={190} height={22} radius={4} />
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Shimmer width={96} height={13} radius={4} />
        <Shimmer width={44} height={24} radius={12} sx={{ ml: 1.5 }} />
      </Box>
    </Box>

    <Grid container spacing={1.5} sx={{ width: '100%' }}>
      {THESIS_FIELDS.map((labelWidth, i) => (
        <Grid item xs={12} sm={6} key={i}>
          <Label width={labelWidth} />
          <Shimmer width="100%" height={THESIS_INPUT} radius={8} />
        </Grid>
      ))}

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Label width={110} />
          <Shimmer width={16} height={16} radius={8} sx={{ mb: LABEL_GAP }} />
        </Box>
        <Shimmer width="100%" height={CRITERIA_ROWS} radius={8} />
      </Grid>

      <Grid item xs={12}>
        <SaveButton />
      </Grid>
    </Grid>
  </Card>
);

const MAIL_TYPES = [150, 172, 88];

const EmailTemplateCard = ({ theme, editorBody }) => (
  <Card theme={theme}>
    <Heading width={250} />

    {/* To */}
    <Box sx={{ width: '100%' }}>
      <Label width={22} />
      <Shimmer width="100%" height={EMAIL_INPUT} radius={8} />
    </Box>

    {/* Mail Type - a row of radios */}
    <Box sx={{ width: '100%', pt: 1 }}>
      <Label width={72} />
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap', gap: '0.5rem' }}>
        {MAIL_TYPES.map((width, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Shimmer width={20} height={20} radius={10} />
            <Shimmer width={width} height={13} radius={4} />
          </Box>
        ))}
      </Box>
    </Box>

    {/* Subject */}
    <Box sx={{ width: '100%', pt: 1 }}>
      <Label width={58} />
      <EditorBlock theme={theme} bodyHeight={editorBody} />
      <ChipRow />
    </Box>

    {/* Message */}
    <Box sx={{ width: '100%', pt: 1 }}>
      <Label width={68} />
      <EditorBlock theme={theme} bodyHeight={editorBody} />
      <ChipRow />
      <SaveButton />
    </Box>
  </Card>
);

const TableCustomizationCard = ({ theme }) => (
  <Card theme={theme}>
    <Heading width={200} />
    <Box sx={{ width: '100%' }}>
      <Shimmer width={112} height={16} radius={4} />
      <Box sx={{ mt: '0.5rem' }}>
        <Shimmer width="100%" height={EMAIL_INPUT} radius={8} />
      </Box>
    </Box>
    <SaveButton />
  </Card>
);

const DealFlowSkeleton = ({ section = 'deal-flow' }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const editorBody = isSmallScreen || isMediumScreen ? 170 : 150;

  return (
    <Box sx={{ display: 'grid', gap: '1rem' }}>
      {section === 'thesis' ? (
        <ThesisCard theme={theme} />
      ) : (
        <>
          <EmailTemplateCard theme={theme} editorBody={editorBody} />
          <TableCustomizationCard theme={theme} />
        </>
      )}
    </Box>
  );
};

export default DealFlowSkeleton;
